import { IChildrenProps } from '@/utils/typings/children-props'
import { ReactNode, useEffect, useRef } from 'react'
import { IAccessRule } from '@/features/access-control/access-control-helpers'
import { useAccessControl } from './use-access-control'

type AccessControlProps = IChildrenProps & {
    /**
     * Access rule that must pass for children to be rendered.
     */
    rule: IAccessRule

    /**
     * Optional fallback to render when access is denied or while necessary data is still loading.
     *
     * @default () => null
     */
    renderFallback?: (hasAccess: false) => ReactNode

    /**
     * Optional callback to render while the access is still being computed
     * Useful for showing e.g. still a partial UI without API calls while the access is being computed
     *
     * @default () => null
     */
    renderWhileLoading?: () => ReactNode

    /**
     * Optional callback to execute when access is denied
     * Use this for redirects, analytics, or other side effects
     */
    onDeny?: () => void
}

/**
 * Component that conditionally renders children based on access rules
 */
export function AccessControl({
    rule,
    children,
    renderFallback = () => null,
    onDeny,
    renderWhileLoading = () => null,
}: AccessControlProps) {
    const hasAccess = useAccessControl(rule)

    const onDenyExecutedRef = useRef(false)

    useEffect(() => {
        // Only trigger onDeny when access is explicitly denied (false), not while loading (undefined)
        if (hasAccess === false && onDeny && !onDenyExecutedRef.current) {
            onDeny()
            onDenyExecutedRef.current = true
        }
    }, [hasAccess, onDeny])

    // Don't render anything while the computation for access is still in progress
    if (hasAccess === undefined) return renderWhileLoading()

    if (!hasAccess) return renderFallback(hasAccess)

    return children
}
